// ======================================================
// FILE: src/pages/AdminLeads.jsx
// Purpose:
// - Admin leads funnel view with responsive styling
// - Matches the /admin + /admin/analytics UI system (ad-* classes)
// - Adds top admin nav tabs
// - Adds visible feedback for actions (Saved / Copied) + per-row saving state
// ======================================================

import React, { useEffect, useMemo, useRef, useState } from "react";
import { auth } from "../firebase";
import SuperAdminNav from "../components/SuperAdminNav";

const API_BASE =
  (typeof window !== "undefined" && window.__FN_BASE__) ||
  process.env.REACT_APP_FUNCTIONS_BASE_URL ||
  "/api";

function fmt(ms) {
  if (!ms) return "";
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return "";
  }
}

function dayStartMs(v) {
  if (!v) return null;
  const d = new Date(`${v}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

function dayEndMs(v) {
  if (!v) return null;
  const d = new Date(`${v}T23:59:59.999`);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

function csvEscape(value) {
  const s = String(value == null ? "" : value);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

const STAGE_ORDER = [
  "email_captured",
  "register_opened",
  "account_created",
  "checkout_started",
  "checkout_session_created",
  "email_verified",
  "onboarding_complete",
];

function stageRank(s) {
  const i = STAGE_ORDER.indexOf(String(s || "").toLowerCase());
  return i === -1 ? 999 : i;
}

function stageLabel(s) {
  const key = String(s || "").toLowerCase();
  const map = {
    email_captured: "Email Captured",
    register_opened: "Registration Started",
    account_created: "Account Created",
    checkout_started: "Checkout Started",
    checkout_session_created: "Checkout Session Created",
    email_verified: "Email Verified",
    onboarding_complete: "Onboarding Complete",
  };
  return map[key] || s || "";
}

export default function AdminLeads() {
  const [me, setMe] = useState(() => auth.currentUser);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState("");

  const [toast, setToast] = useState("");
  const toastTimer = useRef(null);

  const [rowBusy, setRowBusy] = useState(() => ({}));

  const [filterStage, setFilterStage] = useState("");
  const [hideConverted, setHideConverted] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => auth.onAuthStateChanged((u) => setMe(u)), []);

  useEffect(() => {
    return () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, []);

  function showToast(msg) {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 2200);
  }

  async function load() {
    setErr("");
    if (!me) {
      setItems([]);
      setStats(null);
      return;
    }
    setLoading(true);
    try {
      const token = await me.getIdToken(true);
      const r = await fetch(`${API_BASE}/admin/leads/list?limit=400`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "same-origin",
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setItems(Array.isArray(j.items) ? j.items : []);
      setStats(j.stats || null);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  const sourceOptions = useMemo(() => {
    const vals = new Set();
    (items || []).forEach((x) => {
      const v =
        String(
          x.referrer ||
          x.source ||
          x.utm?.utm_source ||
          ""
        ).trim();
      if (v) vals.add(v);
    });
    return Array.from(vals).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const filtered = useMemo(() => {
    const qq = (q || "").trim().toLowerCase();
    const fromMs = dayStartMs(fromDate);
    const toMs = dayEndMs(toDate);

    return (items || [])
      .filter((x) => !hideConverted || !["converted", "do_not_contact"].includes(String(x.status || "").toLowerCase()))
      .filter((x) => !filterStage || String(x.stage || "").toLowerCase() === filterStage)
      .filter((x) => !filterStatus || String(x.status || "").toLowerCase() === filterStatus)
      .filter((x) => {
        if (!filterSource) return true;
        const src = String(x.referrer || x.source || x.utm?.utm_source || "").trim();
        return src === filterSource;
      })
      .filter((x) => !qq || String(x.email || "").toLowerCase().includes(qq))
      .filter((x) => {
        const ts = x.lastSeenAt || x.createdAt || 0;
        if (fromMs != null && ts < fromMs) return false;
        if (toMs != null && ts > toMs) return false;
        return true;
      })
      .sort(
        (a, b) =>
          (b.lastSeenAt || b.createdAt || 0) - (a.lastSeenAt || a.createdAt || 0) ||
          stageRank(a.stage) - stageRank(b.stage)
      );
  }, [items, filterStage, filterStatus, filterSource, fromDate, toDate, q, hideConverted]);

  async function updateLead(leadId, patch) {
    setErr("");
    if (!me) return;
    try {
      setRowBusy((m) => ({ ...m, [leadId]: true }));
      const token = await me.getIdToken(true);
      const r = await fetch(`${API_BASE}/admin/leads/update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "same-origin",
        body: JSON.stringify({ leadId, ...patch }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || `HTTP ${r.status}`);

      setItems((prev) =>
        (prev || []).map((it) =>
          it.leadId === leadId ? { ...it, ...patch } : it
        )
      );
      showToast("Saved");

      void load();
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setRowBusy((m) => {
        const next = { ...m };
        delete next[leadId];
        return next;
      });
    }
  }

  async function deleteLead(leadId) {
    if (!window.confirm("Delete this lead permanently? This cannot be undone.")) return;
    setErr("");
    if (!me) return;
    try {
      setRowBusy((m) => ({ ...m, [leadId]: true }));
      const token = await me.getIdToken(true);
      const r = await fetch(`${API_BASE}/admin/leads/delete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "same-origin",
        body: JSON.stringify({ leadId }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setItems((prev) => (prev || []).filter((it) => it.leadId !== leadId));
      showToast("Deleted");
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setRowBusy((m) => {
        const next = { ...m };
        delete next[leadId];
        return next;
      });
    }
  }

  function askNotes(current) {
    const next = window.prompt("Notes for this lead:", current || "");
    if (next === null) return null;
    return next;
  }

  function exportCsv() {
    const rows = filtered.map((x) => ({
      email: x.email || "",
      leadId: x.leadId || "",
      stage: x.stage || "",
      status: x.status || "",
      lastSeenAt: fmt(x.lastSeenAt),
      createdAt: fmt(x.createdAt),
      source: x.referrer || x.source || x.utm?.utm_source || "",
      utm_source: x.utm?.utm_source || "",
      utm_campaign: x.utm?.utm_campaign || "",
      notes: x.notes || "",
    }));

    const headers = [
      "email",
      "leadId",
      "stage",
      "status",
      "lastSeenAt",
      "createdAt",
      "source",
      "utm_source",
      "utm_campaign",
      "notes",
    ];

    const csv = [
      headers.join(","),
      ...rows.map((row) => headers.map((h) => csvEscape(row[h])).join(",")),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `admin-leads-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    showToast("CSV exported");
  }

  return (
    <div className="ad ad-wrap">
      <AdStyles />

      <SuperAdminNav title="Admin" active="leads" />

      {!me ? (
        <div className="ad-banner ad-banner--error">Please sign in as an admin user to view leads.</div>
      ) : null}

      {err ? (
        <div className="ad-banner ad-banner--error">
          <b>Error:</b> {err}
        </div>
      ) : null}

      {toast ? (
        <div className="ad-banner" style={{ background: "#ecfdf5", borderColor: "#a7f3d0", color: "#065f46" }}>
          {toast}
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        <button onClick={load} disabled={!me || loading} className="ad-btn ad-btn--primary">
          {loading ? "Refreshing…" : "Refresh"}
        </button>

        <button onClick={exportCsv} disabled={!filtered.length} className="ad-btn">
          Export CSV
        </button>

        <label>
          Stage{" "}
          <select value={filterStage} onChange={(e) => setFilterStage(e.target.value)} className="ad-input">
            <option value="">All</option>
            {STAGE_ORDER.map((s) => (
              <option key={s} value={s}>
                {stageLabel(s)}
              </option>
            ))}
          </select>
        </label>

        <label>
          Status{" "}
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="ad-input">
            <option value="">All</option>
            <option value="new">new</option>
            <option value="contacted">contacted</option>
            <option value="converted">converted</option>
            <option value="do_not_contact">do_not_contact</option>
          </select>
        </label>

        <label>
          Source{" "}
          <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)} className="ad-input">
            <option value="">All</option>
            {sourceOptions.map((s) => (
              <option key={s} value={s}>
                {stageLabel(s)}
              </option>
            ))}
          </select>
        </label>

        <label>
          From{" "}
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="ad-input"
          />
        </label>

        <label>
          To{" "}
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="ad-input"
          />
        </label>

        <input
          placeholder="Search email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="ad-input"
          style={{ minWidth: 260 }}
        />

        <button
          onClick={() => {
            setFilterStage("");
            setFilterStatus("");
            setFilterSource("");
            setFromDate("");
            setToDate("");
            setQ("");
          }}
          className="ad-btn"
        >
          Clear filters
        </button>

        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#374151", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={hideConverted}
            onChange={(e) => setHideConverted(e.target.checked)}
          />
          Hide converted &amp; DNC
        </label>

        {stats ? (
          <div className="ad-muted" style={{ fontSize: 13 }}>
            <b>Total:</b> {stats.total || 0}
          </div>
        ) : null}

        <div className="ad-muted" style={{ fontSize: 13 }}>
          <b>Showing:</b> {filtered.length}
        </div>
      </div>

      <div className="ad-table-wrap">
        <table className="ad-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Stage</th>
              <th>Status</th>
              <th>Last seen</th>
              <th>UTM</th>
              <th className="ad-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((x) => (
              <tr key={x.leadId} style={x.status === "do_not_contact" ? { opacity: 0.45, background: "#f5f5f5" } : {}}>
                <td data-label="Email" className="ad-break">
                  <div style={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span>{x.email || ""}</span>
                    {x.notes ? <span className="ad-badge">📝 notes</span> : null}
                  </div>
                  <div className="ad-muted" style={{ fontSize: 12 }}>
                    {x.leadId}
                  </div>
                </td>
                <td data-label="Stage">{stageLabel(x.stage)}</td>
                <td data-label="Status">{x.status || ""}</td>
                <td data-label="Last seen">{fmt(x.lastSeenAt)}</td>
                <td data-label="UTM" style={{ fontSize: 12 }}>
                  {x.utm && (x.utm.utm_source || x.utm.utm_campaign) ? (
                    <div>
                      <div>
                        <b>src:</b> {x.utm.utm_source || ""}
                      </div>
                      <div>
                        <b>cmp:</b> {x.utm.utm_campaign || ""}
                      </div>
                    </div>
                  ) : (
                    <span className="ad-muted">—</span>
                  )}
                </td>
                <td data-label="Actions" className="ad-actions">
                  <div className="ad-row ad-wrap ad-gap" style={{ justifyContent: "flex-end" }}>
                    <button
                      onClick={() => updateLead(x.leadId, { status: "contacted" })}
                      className="ad-btn"
                      disabled={!me || !!rowBusy[x.leadId]}
                      title="Sets status=contacted"
                    >
                      Contacted
                    </button>
                    <button
                      onClick={() => updateLead(x.leadId, { status: "converted" })}
                      className="ad-btn ad-btn--primary"
                      disabled={!me || !!rowBusy[x.leadId]}
                      title="Sets status=converted"
                    >
                      Converted
                    </button>
                    <button
                      onClick={() => updateLead(x.leadId, { status: "do_not_contact" })}
                      className="ad-btn ad-btn--danger"
                      disabled={!me || !!rowBusy[x.leadId]}
                      title="Sets status=do_not_contact"
                    >
                      DNC
                    </button>
                    <button
                      onClick={() => {
                        const next = askNotes(x.notes || "");
                        if (next === null) return;
                        updateLead(x.leadId, { notes: next });
                      }}
                      className="ad-btn"
                      disabled={!me || !!rowBusy[x.leadId]}
                      title="Edit notes"
                    >
                      Notes
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          await navigator.clipboard?.writeText(x.email || "");
                          showToast("Copied");
                        } catch {
                          showToast("Copy failed");
                        }
                      }}
                      className="ad-btn"
                      disabled={!me || !!rowBusy[x.leadId]}
                      title="Copy email"
                    >
                      Copy
                    </button>
                    <button
                      onClick={() => deleteLead(x.leadId)}
                      className="ad-btn ad-btn--danger"
                      disabled={!me || !!rowBusy[x.leadId]}
                      title="Permanently delete this lead"
                    >
                      Delete
                    </button>
                    {rowBusy[x.leadId] ? (
                      <span className="ad-muted" style={{ fontSize: 12 }}>
                        Saving…
                      </span>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}

            {!filtered.length ? (
              <tr>
                <td colSpan={6} className="ad-empty">
                  No leads match your filters.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <div className="ad-muted" style={{ marginTop: 14, fontSize: 13 }}>
        Tip: after clicking an action, you should see “Saved” and the Status column should update. If it doesn’t,
        scroll up for any error message.
      </div>
    </div>
  );
}

function AdStyles() {
  return (
    <style>{`
      .ad *, .ad *::before, .ad *::after { box-sizing: border-box; }
      .ad-wrap{max-width:1200px;margin:24px auto;padding:0 16px;width:100%}
      .ad-header{display:flex;justify-content:space-between;align-items:center;gap:12px;margin:8px 0 16px}
      .ad-tabs{display:flex;gap:8px;flex-wrap:wrap}
      .ad-tab{padding:10px 14px;border:1px solid #d6d6e7;border-radius:10px;background:#fff;cursor:pointer;line-height:1;text-decoration:none;color:inherit}
      .ad-tab.is-active{background:#111827;color:#fff;border-color:#111827}

      .ad-input{height:38px;padding:0 12px;border:1px solid #d6d6e7;border-radius:10px;background:#fff}
      .ad-btn{height:38px;padding:0 14px;border-radius:10px;border:1px solid #cfcfe6;background:#fff;cursor:pointer;display:inline-flex;align-items:center;justify-content:center}
      .ad-btn--primary{background:#4f46e5;color:#fff;border-color:#4f46e5}
      .ad-btn--danger{background:#dc2626;color:#fff;border-color:#dc2626}
      .ad-btn:disabled{opacity:.6;cursor:not-allowed}

      .ad-banner{padding:10px 12px;border-radius:10px;margin:8px 0;border:1px solid}
      .ad-banner--error{background:#fee2e2;color:#7a0b0b;border-color:#fecaca}

      .ad-table-wrap{width:100%;overflow:auto}
      .ad-table{width:100%;border-collapse:separate;border-spacing:0;border:1px solid #e5e7eb;border-radius:12px;overflow:hidden;background:#fff}
      .ad-table thead th{background:#f9fafb;text-align:left;font-weight:600;padding:12px;border-bottom:1px solid #e5e7eb;white-space:nowrap}
      .ad-table td{padding:12px;border-top:1px solid #f3f4f6;vertical-align:middle}
      .ad-actions{text-align:right;white-space:nowrap}
      .ad-empty{padding:14px;text-align:center}
      .ad-break{word-break:break-word;overflow-wrap:anywhere}
      .ad-muted{color:#6b7280}

      @media (max-width: 720px){
        .ad-table thead{display:none}
        .ad-table tr{display:block;border-bottom:1px solid #f3f4f6}
        .ad-table td{display:flex;justify-content:space-between;gap:12px}
        .ad-table td::before{content:attr(data-label);font-weight:600;color:#6b7280}
        .ad-actions{justify-content:flex-end}
      }

      .ad-row{display:flex;align-items:center}
      .ad-gap{gap:8px}
      .ad-wrap{flex-wrap:wrap}

      .ad-badge{display:inline-flex;align-items:center;gap:6px;padding:2px 8px;border-radius:999px;border:1px solid #e5e7eb;font-size:12px;line-height:18px}
    `}</style>
  );
}
